// src/components/Reports/widgets/DetailsWidget.tsx

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MoreVertical } from 'lucide-react';
import DashboardReportService from '@services/DashboardReportService';
import type {
  ReportWidget,
  ReportFilters,
  DetailsData,
  LayerData,
  NewSettings,
} from '@services/DashboardReportService';
import { ReportDatePicker } from '../ReportDatePicker';
import { ReportSelect } from '../ReportSelect';
import { Modal } from '@/components/utils/Modal';
import DetailsSettings from './DetailsWidgetSettings';

interface DetailsWidgetProps {
  widget: ReportWidget;
  dashboard: { DashboardId: number; ReportId: number; Name?: string };
  filters: ReportFilters;
  onRemoveWidget: (widget: ReportWidget) => void;
  onUpdateSettings?: (widgetId: number, newSettings: NewSettings) => void;
  layers: LayerData[];
}

function addOneDayOffset(dateStr: string | null): string
{
  if (!dateStr) return ' - ';
  try
  {
    const d = new Date(dateStr.substring(0, 10));
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString();
  }
  catch
  {
    return ' - ';
  }
}

function formatDateInput(d: Date): string
{
  return d.toISOString().split('T')[0]!;
}

export const DetailsWidget: React.FC<DetailsWidgetProps> = ({
  widget,
  dashboard,
  filters,
  onRemoveWidget,
  onUpdateSettings,
  layers,
}) =>
{
  const settings = (widget.DataValuesJson?.Settings ?? {}) as Record<string, unknown>;

  const today = new Date();
  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);

  const [data, setData] = useState<DetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const optionsLayers = [
    { value: -1, label: 'All Layers' },
    ...layers.map(l => ({ value: l.LayerId, label: l.Name })),
  ];
  const [selectedLayer, setselectedLayer] = useState(
    optionsLayers.find(x => x.value === (settings.LayerId ? Number(settings.LayerId) : -1)),
  );

  const [startDate, setStartDate] = useState<string>(
    settings.StartDate ? String(settings.StartDate) : formatDateInput(today),
  );
  const [dueDate, setDueDate] = useState<string>(
    settings.DueDate ? String(settings.DueDate) : formatDateInput(nextWeek),
  );

  const [showSettings, setShowSettings] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () =>
  {
    try
    {
      setLoading(true);
      setError(null);
      const result = await DashboardReportService.getReportDetails({
        dashboardId: dashboard.DashboardId,
        widgetId: widget.WidgetId,
        filters,
      });
      setData(result);
    }
    catch
    {
      setError('Failed to load details.');
    }
    finally
    {
      setLoading(false);
    }
  }, [dashboard.DashboardId, widget.WidgetId, filters]);

  useEffect(() =>
  {
    loadData();
  }, [loadData]);

  useEffect(() =>
  {
    const handleClick = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const saveAndReload = useCallback(
    async (layerId: number, start: string, due: string) =>
    {
      try
      {
        await DashboardReportService.updateWidgetDetailsSettings({
          ReportId: widget.ReportId,
          WidgetId: widget.WidgetId,
          LayerId: layerId,
          StartDate: start,
          DueDate: due,
        });
        await loadData();
      }
      catch
      {
        // ignore
      }
    },
    [widget.ReportId, widget.WidgetId, loadData],
  );

  const handleLayerChange = (layer: { value: number; label: string } | null) =>
  {
    setselectedLayer(layer ?? undefined);
    saveAndReload(layer?.value ?? -1, startDate, dueDate);
  };

  const handleDateChange = (field: 'start' | 'due', value: string) =>
  {
    const newStart = field === 'start' ? value : startDate;
    const newDue = field === 'due' ? value : dueDate;
    if (field === 'start') setStartDate(value);
    else setDueDate(value);
    saveAndReload(selectedLayer?.value ?? -1, newStart, newDue);
  };

  // Correct field mapping matching legacy Details.js
  const productivity =
    data && data.Capacity > 0
      ? `${Math.min((data.ProductiveHrs / data.Capacity) * 100, 100).toFixed(2)} %`
      : '0 %';

  const efficiency =
    data && data.LoggedHours > 0
      ? `${Math.min((data.ProductiveHrs / data.LoggedHours) * 100, 100).toFixed(2)} %`
      : '0 %';

  const statCards: { label: string; value: string | number }[] = data
    ? [
      // Row 1
      { label: 'Days until Due\nDate', value: data.DaysUntilDueDate ?? 0 },
      { label: 'Capacity', value: data.Capacity ?? 0 },
      { label: 'Estimated Hours', value: data.Estimated ?? 0 },
      { label: 'Remaining Hours', value: data.Remaining ?? 0 },
      { label: 'Logged Hrs\nPrevious Day', value: data.HoursPerDayVelocity ?? 0 },
      { label: 'Logged Hrs Today', value: data.CurrentVelocity ?? 0 },
      // Row 2
      {
        label: 'Days until cards\nare finished at this\nvelocity',
        value: data.DaysUntilFinishedAtVelocity ?? 0,
      },
      {
        label: 'Predicted date of\ncompletion at this\nvelocity',
        value: addOneDayOffset(data.PredictedDate),
      },
      { label: 'Velocity to finish\nin time', value: data.VelocityToFinish ?? 0 },
      { label: '# People\nAssigned', value: data.PeopleAssigned ?? 0 },
      { label: '# PTO Days', value: data.PTODays ?? 0 },
      { label: 'Productivity', value: productivity },
      // Row 3
      { label: 'Efficiency', value: efficiency },
      { label: 'Custom People\nAssigned', value: data.CustomTeamSize ?? 0 },
      { label: 'Productive Hours', value: data.ProductiveHrs ?? 0 },
    ]
    : [];

  const handleDelete = async () =>
  {
    setMenuOpen(false);
    setDeleteModal(true);
  };

  const deleteWidget = async () =>
  {
    try
    {
      await DashboardReportService.deleteWidget(widget.ReportId, widget.WidgetId);
      onRemoveWidget(widget);
    }
    catch
    {
      // ignore
    }
  };

  const getDataChartNewSettings = (
    _data: unknown,
    _selectedLayer: unknown,
    request: {
      DateDeliveryFieldId?: number;
      EstimatedFieldId?: number;
      RemainingFieldId?: number;
      AddDynamicGroup?: boolean;
      CustomTeamSize?: number | string;
      CardType?: string;
      ConfigValueID?: number;
      LayerId?: number | string;
    },
  ) =>
  {
    const newSettings: NewSettings = {
      LayerId: typeof request.LayerId === 'number' ? request.LayerId : undefined,
      DateDeliveryFieldId: request.DateDeliveryFieldId,
      EstimatedFieldId: request.EstimatedFieldId,
      RemainingFieldId: request.RemainingFieldId,
      AddDynamicGroup: request.AddDynamicGroup,
      CustomTeamSize: request.CustomTeamSize,
      CardType: request.CardType,
      ConfigValueID: request.ConfigValueID,
    };
    setShowSettings(false);
    setLoading(false);
    if (onUpdateSettings) onUpdateSettings(widget.WidgetId, newSettings);

    loadData();
  };

  const isDark = document.documentElement.classList.contains('dark');

  return (
    <>
      <div className="styled-widget-container">
        {/* Header */}
        <div className="widget-header">
          <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? '#a9a093' : '#333' }}>
            {widget.Description || 'Details'}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-[#3a3c3c] rounded dark:text-[#a9a093]"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Widget menu"
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-7 z-50 bg-white dark:bg-[#333535] border border-gray-200 dark:border-[#4a4c4c] rounded shadow-md"
                style={{ minWidth: 140 }}
              >
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093] disabled:cursor-not-allowed"
                  onClick={() =>
                  {
                    setMenuOpen(false);
                    setShowSettings(true);
                  }}
                >
                  Settings
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c]"
                  style={{ color: '#c00' }}
                  onClick={handleDelete}
                >
                  Delete Widget
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Controls row — matches reference: Layer select + plain-text dates */}
        <>
          <div
            className="burndown-header flex justify-center"
            style={{ borderBottom: `1px solid ${isDark ? '#4a4c4c' : '#f6f6f6'}` }}
          >
            {/* Layer dropdown */}
            <ReportSelect
              label="Layer:"
              value={selectedLayer}
              onChange={v => handleLayerChange(v)}
              options={optionsLayers}
              width={140}
              getOptionLabel={o => o.label}
              getOptionValue={o => String(o.value)}
            />

            {/* Start Date */}
            <ReportDatePicker
              label="Start Date"
              value={startDate}
              onChange={v => handleDateChange('start', v)}
            />

            {/* Due Date */}
            <ReportDatePicker
              label="Due Date"
              value={dueDate}
              onChange={v => handleDateChange('due', v)}
            />
          </div>
          {!showSettings && (
            <>
              <div className="widget-content">
                {loading && (
                  <p className="report-widget-feedback-message text-center">Loading details...</p>
                )}
                {error && (
                  <p className="report-widget-feedback-message" style={{ color: '#c00' }}>
                    {error}
                  </p>
                )}
                {!loading && !error && data && (
                  <div className="general-details-report">
                    <div className="Details-row">
                      {statCards.map(card => (
                        <div
                          key={card.label}
                          className="col-sm-2"
                          style={{
                            width: '16.66%',
                            minWidth: 80,
                            textAlign: 'center',
                            padding: '8px 4px',
                          }}
                        >
                          <div className="details-item-figure-cell-Report">{card.value}</div>
                          <div
                            className="details-item-figure-label-cell"
                            style={{ whiteSpace: 'pre-line' }}
                          >
                            {card.label.replace(/\\n/g, '\n')}
                          </div>
                        </div>
                      ))}
                    </div>

                    {data.CurrentVelocity <= 0 && (
                      <p
                        style={{
                          fontSize: 12,
                          fontStyle: 'italic',
                          color: isDark ? '#b1a99d' : '#9c9c94',
                          textAlign: 'center',
                          marginTop: 8,
                        }}
                      >
                        Based on the logged hrs previous day.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {showSettings && (
            <DetailsSettings
              widgetId={widget.WidgetId}
              dashboard={dashboard}
              settings={{
                ...settings,
                StartDate: startDate,
                DueDate: dueDate,
                LayerId: selectedLayer?.value,
              }}
              onCancel={() => setShowSettings(false)}
              onSave={getDataChartNewSettings}
            />
          )}
        </>
      </div>
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Widget"
        preventCloseClickOut
      >
        <p>Are you sure you want to delete it?</p>
        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            onClick={() =>
            {
              deleteWidget();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
          >
            Ok
          </button>
          <button
            type="button"
            onClick={() => setDeleteModal(false)}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};

export default DetailsWidget;
